/**
 * GreenEZ Collection Pages v2 — Shared JS
 * Modal (color-chart-v2 a11y pattern), pill tabs, sort, wishlist
 *
 * Ported from: GreenEZ/design/html-drafts/collection-pages/shared/collection.js
 * Dropped: initMenu() (theme header owns its own menu)
 *
 * Modal a11y spec:
 *   - focus trap inside dialog
 *   - ESC closes
 *   - aria-modal="true" on dialog
 *   - return focus to triggering element on close
 *   - body scroll-lock when open
 *   - click-outside (backdrop) closes
 *   - bottom-sheet below 768px, centered card at 768+
 *
 * Progressive enhancement:
 *   - cards are <a href="/products/handle"> so default click navigates to PDP
 *   - when JS loads, click is intercepted and opens the modal instead
 *   - if JS fails, navigation fallback works
 */

(function () {
  'use strict';

  function getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(function (el) {
      return !el.closest('[hidden]') && el.offsetParent !== null;
    });
  }

  /* ===== MODAL ===== */
  var currentTrigger = null;
  var overlay = null;
  var dialog = null;
  var modalTitle = null;
  var modalOverline = null;
  var modalImgWrap = null;
  var modalPrice = null;
  var modalRating = null;
  var modalDesc = null;
  var modalPrimary = null;
  var modalSecondary = null;

  function initModal() {
    overlay = document.querySelector('.cp-modal-overlay');
    dialog = document.querySelector('.cp-modal');
    if (!overlay || !dialog) return;

    modalOverline   = dialog.querySelector('.cp-modal__overline');
    modalTitle      = dialog.querySelector('.cp-modal__title');
    modalImgWrap    = dialog.querySelector('.cp-modal__img-wrap');
    modalPrice      = dialog.querySelector('.cp-modal__price');
    modalRating     = dialog.querySelector('.cp-modal__rating');
    modalDesc       = dialog.querySelector('.cp-modal__desc');
    modalPrimary    = dialog.querySelector('.cp-modal__cta-primary');
    modalSecondary  = dialog.querySelector('.cp-modal__cta-secondary');

    var closeBtn = dialog.querySelector('.cp-modal__close');
    if (closeBtn) closeBtn.addEventListener('click', closeModal);

    overlay.addEventListener('mousedown', function (e) {
      if (e.target === overlay) closeModal();
    });

    document.addEventListener('keydown', function (e) {
      if (!overlay.classList.contains('is-open')) return;
      if (e.key === 'Escape') { e.preventDefault(); closeModal(); return; }
      if (e.key === 'Tab') trapTab(e);
    });
  }

  function trapTab(e) {
    var focusable = getFocusable(dialog);
    if (!focusable.length) { e.preventDefault(); return; }
    var first = focusable[0];
    var last  = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
    }
  }

  function openModal(data, trigger) {
    if (!overlay || !dialog) return;
    currentTrigger = trigger;

    if (modalOverline)  modalOverline.textContent  = data.category || 'GreenEZ';
    if (modalTitle)     modalTitle.textContent      = data.title    || '';
    if (modalDesc)      modalDesc.textContent       = data.desc     || '';

    if (modalImgWrap) {
      var img = modalImgWrap.querySelector('.cp-modal__img');
      var ph  = modalImgWrap.querySelector('.cp-modal__img-placeholder');
      if (!img && data.img) {
        img = document.createElement('img');
        img.className = 'cp-modal__img';
        img.loading = 'lazy';
        img.alt = '';
        img.width = 640;
        img.height = 640;
        modalImgWrap.insertBefore(img, modalImgWrap.firstChild);
      }
      if (img) {
        if (data.img) {
          img.src = data.img;
          img.alt = data.title || '';
          img.style.display = 'block';
          if (ph) ph.style.display = 'none';
        } else {
          img.style.display = 'none';
          if (ph) { ph.textContent = data.title || ''; ph.style.display = 'block'; }
        }
      } else if (ph) {
        ph.textContent = data.title || '';
        ph.style.display = 'block';
      }
      modalImgWrap.style.background = data.bg || '#E8EDE2';
    }

    if (modalPrice) {
      var nowEl = modalPrice.querySelector('.cp-modal__price-now');
      var wasEl = modalPrice.querySelector('.cp-modal__price-was');
      if (nowEl) nowEl.textContent = data.price || '';
      if (wasEl) { wasEl.textContent = data.was || ''; wasEl.style.display = data.was ? 'inline' : 'none'; }
    }

    if (modalRating) {
      var stars = modalRating.querySelector('.cp-stars');
      var text  = modalRating.querySelector('.cp-modal__rating-text');
      if (stars) stars.textContent = data.stars || '★★★★★';
      if (text)  text.textContent  = data.rating || '';
    }

    if (modalPrimary) {
      modalPrimary.textContent = data.ctaPrimary || 'View product';
      modalPrimary.href = data.url || '#';
    }
    if (modalSecondary) {
      modalSecondary.href = data.url || '#';
    }

    overlay.classList.add('is-open');
    dialog.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(function () {
      var focusable = getFocusable(dialog);
      if (focusable.length) focusable[0].focus();
    });
  }

  function closeModal() {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    dialog.setAttribute('aria-modal', 'false');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    if (currentTrigger) {
      currentTrigger.focus();
      currentTrigger = null;
    }
  }

  /* ===== CARD CLICK INTERCEPTION =====
   * Only intercept clicks when the quick-view modal is actually rendered
   * (section setting "Enable card click -> product modal" = ON, which emits
   * gez-cp-modal into the DOM). When the modal is disabled, leave the card's
   * native <a href="/products/..."> links alone so cards navigate straight to
   * the PDP, and make the rest of the card body navigate there too.
   */
  function initCards() {
    var hasModal = !!(document.querySelector('.cp-modal-overlay') && document.querySelector('.cp-modal'));
    var cards = document.querySelectorAll('.cp-card[data-product]');
    cards.forEach(function (card) {
      var anchor = card.querySelector('a.cp-card__media-link');

      if (hasModal) {
        /* Modal enabled: hijack clicks and open the quick-view dialog. */
        if (anchor) {
          anchor.addEventListener('click', function (e) {
            e.preventDefault();
            var data = parseProductData(card);
            openModal(data, anchor);
          });
        }
        card.addEventListener('click', function (e) {
          if (e.target.closest('.cp-card__wish')) return;
          if (e.target.closest('.cp-cta-a') || e.target.closest('.cp-cta-b')) return;
          var data = parseProductData(card);
          openModal(data, card);
        });
        card.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            var data = parseProductData(card);
            openModal(data, card);
          }
        });
      } else {
        /* Modal disabled: cards go straight to the PDP.
         * The media link and CTA <a> elements navigate natively (no
         * preventDefault). Make clicks anywhere else on the card body
         * (title, benefits, price) navigate to the same product URL,
         * and support keyboard activation on the focusable card. */
        card.addEventListener('click', function (e) {
          if (e.target.closest('.cp-card__wish')) return;
          if (e.target.closest('a')) return; /* real links navigate on their own */
          var data = parseProductData(card);
          if (data.url) window.location.href = data.url;
        });
        card.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            if (e.target.closest('a')) return;
            e.preventDefault();
            var data = parseProductData(card);
            if (data.url) window.location.href = data.url;
          }
        });
      }
    });
  }

  function parseProductData(card) {
    try { return JSON.parse(card.getAttribute('data-product') || '{}'); }
    catch (e) { return {}; }
  }

  /* ===== PILL TABS ===== */
  function initPills() {
    var tablists = document.querySelectorAll('.cp-pills[role="tablist"]');
    tablists.forEach(function (tablist) {
      var tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          activatePill(tablist, tab);
          filterCards(tablist, tab.getAttribute('data-filter'));
        });
        tab.addEventListener('keydown', function (e) {
          var idx = tabs.indexOf(tab);
          var next = null;
          if (e.key === 'ArrowRight') next = tabs[(idx + 1) % tabs.length];
          if (e.key === 'ArrowLeft')  next = tabs[(idx - 1 + tabs.length) % tabs.length];
          if (e.key === 'Home')       next = tabs[0];
          if (e.key === 'End')        next = tabs[tabs.length - 1];
          if (next) { e.preventDefault(); next.focus(); next.click(); }
        });
      });
    });
  }

  function activatePill(tablist, activeTab) {
    var tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
    tabs.forEach(function (t) {
      t.setAttribute('aria-selected', t === activeTab ? 'true' : 'false');
      t.classList.toggle('is-active', t === activeTab);
      t.tabIndex = t === activeTab ? 0 : -1;
    });
  }

  function filterCards(tablist, filter) {
    var section = tablist.closest('.cp-grid-wrap') || document;
    var cards = section.querySelectorAll('.cp-card[data-category]');
    var visible = 0;
    cards.forEach(function (card) {
      var cat = (card.getAttribute('data-category') || '').toLowerCase();
      var show = !filter || filter === 'all' || cat.split(' ').indexOf(filter.toLowerCase()) !== -1;
      card.style.display = show ? '' : 'none';
      if (show) visible++;
    });
    var countEl = section.querySelector('.cp-subbar__count');
    if (countEl) {
      var total = cards.length;
      countEl.textContent = visible === total
        ? total + ' products'
        : 'Showing ' + visible + ' of ' + total + ' products';
    }
  }

  /* ===== SORT ===== */
  function initSort() {
    var selects = document.querySelectorAll('.cp-sort-select');
    selects.forEach(function (select) {
      select.addEventListener('change', function () {
        var val = select.value;
        var section = select.closest('.cp-grid-wrap') || document;
        var grid = section.querySelector('.cp-grid');
        if (!grid) return;
        var cards = Array.from(grid.querySelectorAll('.cp-card[data-price]'));
        cards.sort(function (a, b) {
          var pa = parseFloat(a.getAttribute('data-price')) || 0;
          var pb = parseFloat(b.getAttribute('data-price')) || 0;
          if (val === 'price-asc')  return pa - pb;
          if (val === 'price-desc') return pb - pa;
          var ra = parseInt(a.getAttribute('data-rank'), 10) || 999;
          var rb = parseInt(b.getAttribute('data-rank'), 10) || 999;
          return ra - rb;
        });
        cards.forEach(function (c) { grid.appendChild(c); });
      });
    });
  }

  /* ===== WISHLIST ===== */
  function initWishlist() {
    var hearts = document.querySelectorAll('.cp-card__wish');
    hearts.forEach(function (btn) {
      var card    = btn.closest('.cp-card');
      var handle  = card ? card.getAttribute('data-handle') : null;
      if (!handle) return;

      var key   = 'cp-fav-' + handle;
      var saved = localStorage.getItem(key) === '1';
      updateHeart(btn, saved);

      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        e.preventDefault();
        var current = localStorage.getItem(key) === '1';
        localStorage.setItem(key, current ? '0' : '1');
        updateHeart(btn, !current);
      });
    });
  }

  function updateHeart(btn, saved) {
    var svg = btn.querySelector('svg');
    if (!svg) return;
    if (saved) {
      svg.setAttribute('fill', '#2C3E2D');
      btn.style.background = '#2C3E2D';
      btn.style.borderColor = '#2C3E2D';
      svg.setAttribute('stroke', '#FFFFFF');
    } else {
      svg.setAttribute('fill', 'none');
      btn.style.background = '';
      btn.style.borderColor = '';
      svg.setAttribute('stroke', '#2C3E2D');
    }
    btn.setAttribute('aria-label', saved ? 'Remove from favorites' : 'Save to favorites');
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
  }

  /* ===== INIT ===== */
  function init() {
    initModal();
    initCards();
    initPills();
    initSort();
    initWishlist();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-init on Shopify Section reload (theme editor) */
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);

})();
